'use client';

// ─────────────────────────────────────────────────────────────────────────────
// StatsBar — per-PM "Your Jobs" KPI strip for the Today tab.
//
// The Revenue & Performance row (Contracted / Collected / Avg Margin / Avg
// Days) used to render below the Job Status row. It was company-wide
// aggregate data — roofing_jobs and billing_invoices had no per-PM filter —
// and not actionable for a field PM standing on a roof. Removed during the
// 6-tab restructure; those numbers live in Financial / Executive dashboards
// where the people who set billing/margin decisions actually work.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';
import Icon from "@/components/ui/AppIcon";
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface KpiCard {
  label: string;
  value: string;
  sub?: string;
  icon: string;
  color: string;
  bg: string;
  trend?: 'up' | 'down' | 'neutral';
}

// Demo fallback values for presentation mode
const DEMO_JOB_KPIS: KpiCard[] = [
  { label: "Active Jobs", value: "0", icon: "BriefcaseIcon", color: "text-primary", bg: "bg-primary/10", trend: 'neutral' },
  { label: "Scheduled", value: "0", icon: "CalendarDaysIcon", color: "text-secondary", bg: "bg-secondary/10", trend: 'neutral' },
  { label: "Delayed", value: "0", icon: "ExclamationTriangleIcon", color: "text-warning", bg: "bg-warning/10", trend: 'neutral' },
  { label: "Completed (Mo.)", value: "0", icon: "CheckCircleIcon", color: "text-success", bg: "bg-success/10", trend: 'neutral' },
];

export default function StatsBar() {
  const { currentUser } = useAuth();
  const pmId = currentUser.assigned_pm_id;

  const [jobKpis, setJobKpis] = useState<KpiCard[]>(DEMO_JOB_KPIS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    async function fetchKpis() {
      try {
        // Jobs table is per-PM scoped via assigned_pm_id — these counts
        // are the four cards a field PM actually wants at a glance.
        const { data: jobs } = await supabase
          .from('jobs')
          .select('id, job_status, progress, value, start_date, end_date, created_at')
          .eq('assigned_pm_id', 'pm-john-001');

        const allJobs = jobs ?? [];

        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

        const active = allJobs.filter(j => ['in-progress', 'in_progress'].includes(j.job_status)).length;
        const scheduled = allJobs.filter(j => j.job_status === 'scheduled').length;
        const delayed = allJobs.filter(j => j.job_status === 'delayed').length;
        const completedThisMonth = allJobs.filter(j =>
          j.job_status === 'completed' && j.end_date && j.end_date >= monthStart.split('T')[0]
        ).length;

        if (allJobs.length > 0) {
          setJobKpis([
            { label: "Active Jobs", value: String(active), icon: "BriefcaseIcon", color: "text-primary", bg: "bg-primary/10", trend: 'up' },
            { label: "Scheduled", value: String(scheduled), icon: "CalendarDaysIcon", color: "text-secondary", bg: "bg-secondary/10" },
            { label: "Delayed", value: String(delayed), icon: "ExclamationTriangleIcon", color: "text-warning", bg: "bg-warning/10", trend: delayed > 0 ? 'down' : 'neutral' },
            { label: "Completed (Mo.)", value: String(completedThisMonth), icon: "CheckCircleIcon", color: "text-success", bg: "bg-success/10", trend: 'up' },
          ]);
        }
      } catch {
        // Keep demo defaults on error
      } finally {
        setLoading(false);
      }
    }

    fetchKpis();
  }, [pmId, currentUser.first_name]);

  const renderCard = (stat: KpiCard) => (
    <div key={stat.label} className="bg-card rounded-xl p-4 shadow-sm border border-border flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon name={stat.icon as any} size={20} className={stat.color} variant="solid" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="font-heading text-xl font-bold text-foreground leading-tight">
            {loading ? <span className="inline-block w-10 h-5 bg-muted rounded animate-pulse" /> : stat.value}
          </p>
          {!loading && stat.trend === 'up' && (
            <Icon name="ArrowTrendingUpIcon" size={14} className="text-success flex-shrink-0" variant="solid" />
          )}
          {!loading && stat.trend === 'down' && (
            <Icon name="ArrowTrendingDownIcon" size={14} className="text-warning flex-shrink-0" variant="solid" />
          )}
        </div>
        <p className="font-caption text-xs text-muted-foreground truncate">{stat.label}</p>
        {stat.sub && !loading && (
          <p className="font-caption text-[10px] text-muted-foreground/70 truncate">{stat.sub}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <div>
        <p className="font-caption text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 px-0.5">Job Status</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {jobKpis.map(renderCard)}
        </div>
      </div>
    </div>
  );
}